/* eslint-disable react/prop-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
// AppRoutes.tsx
import { lazy, Suspense, useMemo, useEffect } from 'react';
import { createBrowserRouter, RouterProvider, Outlet, useLocation, Navigate } from 'react-router-dom';
import { suspenseWrapper } from '../utils/suspenseWrapper';

// Keep guards/utilities eagerly loaded
import PrivateRoute from './PrivateRoutes';
import { initGA, logPageView } from '../utils/google-analytics';
import { useInstance } from '../utils/InstaceContext';
import { useAuth } from '../utils/AuthContext';

interface RootLayoutProps {
  value: any
}

const RootLayout: React.FC<RootLayoutProps> = (props) => {
  // eslint-disable-next-line react/prop-types
  const { instance  } = props.value;
  const { user } = useAuth();

  const location = useLocation();
  useEffect(() => {
    const gaView = !user || !user?.email || !(user && user?.email && instance?.GAskipList?.includes(user.email));
    // localStorage.setItem('skip_ga_analytics','yes');
    if(gaView){
      logPageView(location.pathname, document.title);
    }
  }, [location,user,instance]);

  return <Outlet />;
};




// This component wraps all routes and tracks page views
// function RootLayout:REc(props:unknown) {

// }

// Lazily loaded pages (each import creates its own chunk)
const HomeComponent = lazy(() => import('../components/home-component/HomeComponent'));
const RegisterComponent = lazy(() => import('../components/auth/register/RegisterComponent'));
const DashboardComponent = lazy(() => import('../components/dashboard/DashboardComponent'));
const NewQuoteFormApplication = lazy(
  () => import('../components/dashboard/newQuoteFormApplication/newQuoteFormComponent')
);
const ContactComponent = lazy(() => import('../components/contact-us/ContactComponent'));
const QuoteDetailsComponent = lazy(
  () => import('../components/dashboard/quoteDetails/QuoteDetailsComponent')
);
const ForgotPassword = lazy(() => import('../components/auth/forgot-password/ForgotPassword'));
const ResetPassword = lazy(() => import('../components/auth/reset-password/ResetPassword'));
const AdminDashboardComponent = lazy(() => import('../components/dashboard/admin'));
const CurrentApplication = lazy(() => import('../components/dashboard/CurrentApplication/CurrentApplication'));
const CommercialForm = lazy(() => import('../components/dashboard/commercialForm/CommercialForm'));

/**
 * Export ONLY a component from this file to keep React Fast Refresh happy.
 * If you need the router object elsewhere, lift the createBrowserRouter(...)
 * into another file and ensure that file doesn't also export components.
 */
export default function AppRoutes() {
  const { instance } =  useInstance();
  const { user } = useAuth();
  const router = useMemo(
    () =>
      createBrowserRouter([
        {
          element: <RootLayout value = {{ instance,user }}  />, // Root layout with analytics tracking
          children: [
            {
              path: '/',
              element: suspenseWrapper(<HomeComponent />),
              errorElement: suspenseWrapper(<HomeComponent />)
            },
            {
              path: '/register',
              element: suspenseWrapper(<RegisterComponent />),
              errorElement: suspenseWrapper(<RegisterComponent />)
            },
            {
              path: '/contact-us',
              element: suspenseWrapper(<ContactComponent />),
              errorElement: suspenseWrapper(<ContactComponent />)
            },
            {
              path: '/dashboard',
              element: suspenseWrapper(
                <PrivateRoute>
                  <DashboardComponent />
                </PrivateRoute>
              ),
              errorElement: suspenseWrapper(<HomeComponent />)
            },
            {
              path: '/admin',
              element: suspenseWrapper(
                <PrivateRoute>
                  <AdminDashboardComponent />
                </PrivateRoute>
              )
            },
            {
              path: '/product-details',
              element: suspenseWrapper(<NewQuoteFormApplication />)
            },
            {
              path: '/application-details',
              element: suspenseWrapper(<CurrentApplication />)
            },
            {
              path: '/quote-details',
              element: suspenseWrapper(
                <PrivateRoute>
                  <QuoteDetailsComponent />
                </PrivateRoute>
              )
            },
            {
              path: '/commercial-form',
              element: suspenseWrapper(
                <PrivateRoute>
                  <CommercialForm />
                </PrivateRoute>
              )
            },
            {
              path: '/onboarding/forgot-password',
              element: suspenseWrapper(<ForgotPassword />)
            },
            {
              path: '/reset-password',
              element: suspenseWrapper(<ResetPassword />)
            },
            {
              path: '/login',
              element: <Navigate to="/" replace />
            }
          ]
        }
      ]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );



  useEffect(() => {
    // if(user && user?.email && instance?.GAskipList?.includes(user.email)){
    //   localStorage.setItem('skip_ga_analytics','yes');
    // }
    const gaView = !user || !user?.email || !(user && user?.email && instance?.GAskipList?.includes(user.email));
    // Initialize GA4 when app loads
    if(gaView){
      initGA();
    }

  }, [instance,user]);

  return (
    <Suspense fallback={<div>Loading…</div>}>
      <RouterProvider router={router} />
    </Suspense>
  );
}
